import { useState } from 'react'
import { rueckfrage } from '../../components/Rueckfrage'
import { useTexte } from '../../texte'
import {
  useDateiabgleich,
  useFehlendeRaeumen,
  useHerrenloseLoeschen,
  useMedienpruefung,
  useSettings,
} from '../../hooks/queries'
import { groesse } from '../../lib/format'
import { PhotoIcon, VideoIcon, WelleIcon } from '../../components/Icons'

/**
 * Welche Dateien im Bestand nicht überall abspielbar sind (#41).
 *
 * **Warum es diese Karte gibt.** Aus dem Betrieb: „wie können wir feststellen,
 * welche Bilder / Videos aktuell noch nicht den richtigen Kriterien
 * entsprechen?" Seit dem Umrechnen beim Hochladen landet nichts
 * Unverträgliches mehr auf dem Server — was **vorher** hochgeladen wurde,
 * liegt aber weiter da, und die Datenbank sagt es nicht: `mime` nennt bei
 * einem Video den Behälter (`video/mp4`) und nicht die Kodierung. Ein
 * HEVC-Video steht dort wie ein H.264-Video.
 *
 * **Auf Knopfdruck.** Die Prüfung sieht auf dem Server in jede Datei hinein;
 * das ist keine Nebenrechnung, die beim Öffnen der Einstellungen von selbst
 * losläuft.
 *
 * **Und sie listet nur.** Umwandeln kann der Server nicht — kein ffmpeg, das
 * ist die Zielumgebung, und daran ändert diese Karte nichts. Was hier steht,
 * ist eine Arbeitsliste: den Beitrag neu schicken, dann geht die Datei durch
 * dasselbe Umrechnen wie jede andere. Deshalb steht die Reise daneben.
 *
 * # Und der Abgleich von Dateien und Einträgen (#20)
 *
 * **Zwei Knöpfe in einer Karte und nicht einer für beides.** Die
 * Kodierungsprüfung liest in jede Datei hinein, der Abgleich geht das
 * Verzeichnis durch — zwei verschiedene Kosten und zwei verschiedene Fragen.
 *
 * Er findet zwei Fehler, die die Speicherkarte **nicht** kennt: dort geht es
 * um Zeilen, die an nichts hängen — Zeile und Datei sind vorhanden, nur die
 * Verknüpfung fehlt. Hier fehlt jeweils eine der beiden Seiten, und beides
 * verfälscht die Speicheranzeige: eine Datei ohne Eintrag belegt Platz, den
 * keine Zahl kennt, ein Eintrag ohne Datei zählt Bytes mit, die es nicht gibt.
 *
 * ## Der Fundort und die beiden Knöpfe
 *
 * Aus dem Betrieb: „entweder den gesamten Pfad angeben, damit man sie am
 * Server findet — oder eine Möglichkeit am Knopf, die Datei anzuzeigen und zu
 * löschen." **Beides steht da**, und das ist keine Unentschlossenheit: der
 * Zufallsname allein reicht per FTP nicht, weil die Datei zwei Ebenen tief
 * liegt (`e128….jpg` in `e1/28/`) — und wer schon hier steht, will sie nicht
 * suchen, sondern ansehen und wegwerfen.
 *
 * **Ansehen ist ein Verweis, kein Knopf.** Er öffnet eine Adresse; ein neuer
 * Reiter lässt die Liste stehen, statt sie durch ein Bild zu ersetzen. Er geht
 * an denselben Ursprung, das Session-Cookie fährt also mit — der Server prüft
 * dort dasselbe wie überall.
 *
 * **Und der Server prüft es im Augenblick des Aufrufs, nicht aus dieser
 * Liste.** Was von hier hinausgeht, ist der Name einer Datei und damit eine
 * Behauptung. Gehört sie inzwischen doch zu einem Beitrag, ist sie über diesen
 * Weg nicht zu bekommen — sonst wäre es ein zweiter Weg zu einem Foto und
 * damit ein zweites Leck. Der Grund steht bei `Speicher::herrenlosePfad()`.
 */
/**
 * Ein Wort mit Zahl, in der richtigen Zahlform.
 *
 * „1 Dateien geprüft" stand im Prüflauf da und liest sich falsch. Zwei Formen
 * reichen hier: das Deutsche unterscheidet Einzahl und Mehrzahl, mehr braucht
 * keine dieser Zeilen.
 */
/**
 * „1 Datei" oder „7 Dateien".
 *
 * Die Wörter kommen aus `texte/de.ts`; hier steht nur die Regel, wann welches
 * gilt. **Eine andere Sprache zählt anders** — dann wandert diese Funktion
 * mit dem Wortlaut zusammen dorthin.
 */
function anzahl(eins: string, viele: string, n: number): string {
  return `${n} ${n === 1 ? eins : viele}`
}

export function MedienCard() {
  const t = useTexte()
  const einstellungen = useSettings()
  // Dieselbe Linie wie bei der Speicherkarte: ohne Decke ist dieses Konto
  // keine Verwaltung, und der Server hat das entschieden.
  const istVerwaltung = einstellungen.data?.ceiling !== undefined
  const pruefung = useMedienpruefung()
  /**
   * Ab welcher Datei der nächste Lauf anfängt.
   *
   * Der Server sieht sich höchstens vierhundert an und gibt mit `weiter` den
   * Namen der letzten zurück. Ohne diesen Zustand nähme jeder Knopfdruck
   * wieder dieselben vierhundert — genau das war der Befund.
   */
  const [ab, setAb] = useState<string | null>(null)
  const abgleich = useDateiabgleich(ab)
  const raeumen = useFehlendeRaeumen()
  const wegwerfen = useHerrenloseLoeschen()

  if (!istVerwaltung) return null

  const daten = pruefung.data
  const gegen = abgleich.data
  /** Nur die, deren **Hauptdatei** fehlt -- die räumt der Knopf weg. */
  const kaputte = (gegen?.fehlend ?? []).filter((z) => z.welche === 'datei').length

  return (
    <div className="card card--seite">
      <h2 className="card__titel">{t.medien.titel}</h2>
      <p className="card__lead">{t.medien.lead}</p>

      <button
        type="button"
        className="button button--outline button--schmal"
        disabled={pruefung.isFetching}
        onClick={() => void pruefung.refetch()}
      >
        {pruefung.isFetching ? t.medien.wirdGeprueft : t.medien.pruefen}
      </button>

      {pruefung.isError && (
        <p role="alert" className="dienst__fehler">{t.medien.pruefungFehlt}</p>
      )}

      {daten !== undefined && (
        <>
          <p className="card__lead card__lead--oben">
            {daten.befunde.length === 0
              ? t.medien.allesGut(anzahl(t.medien.datei, t.medien.dateien, daten.geprueft))
              : t.medien.befunde(
                  anzahl(t.medien.datei, t.medien.dateien, daten.geprueft),
                  daten.befunde.length,
                )}
            {/* **Ein abgeschnittener Lauf sagt das.** Sonst sähe eine halbe
                Liste vollständig aus. */}
            {daten.offen > 0 && t.medien.offen(daten.offen)}
          </p>

          {daten.befunde.length > 0 && (
            <ul className="medienliste">
              {daten.befunde.map((fund) => (
                <li key={fund.id} className="medienliste__zeile">
                  <span className="medienliste__zeichen" aria-hidden="true">
                    {fund.kind === 'video'
                      ? <VideoIcon size={18} />
                      : fund.kind === 'audio'
                        ? <WelleIcon size={18} />
                        : <PhotoIcon size={18} />}
                  </span>
                  <span className="medienliste__text">
                    <strong>{fund.befund}</strong>
                    <span className="medienliste__dazu">
                      {fund.trip_title} · {fund.mime}
                      {fund.codec !== '' && ` · ${fund.codec}`} · {groesse(fund.bytes)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <hr className="medien__trenner" />

      <h3 className="medien__untertitel">{t.medien.abgleichTitel}</h3>
      <p className="card__lead">{t.medien.abgleichLead}</p>

      <button
        type="button"
        className="button button--outline button--schmal"
        disabled={abgleich.isFetching}
        onClick={() => void abgleich.refetch()}
      >
        {abgleich.isFetching ? t.medien.wirdAbgeglichen : t.medien.abgleichen}
      </button>

      {abgleich.isError && (
        <p role="alert" className="dienst__fehler">{t.medien.abgleichFehlt}</p>
      )}

      {gegen !== undefined && (
        <>
          <p className="card__lead card__lead--oben">
            <strong>
              {t.medien.ohneEintrag(anzahl(t.medien.datei, t.medien.dateien, gegen.herrenlos.length))}
            </strong>
            {gegen.herrenlos.length > 0 && ` (${groesse(gegen.herrenlos_bytes)})`} ·{' '}
            <strong>
              {t.medien.ohneDatei(anzahl(t.medien.eintrag, t.medien.eintraege, gegen.fehlend.length))}
            </strong>.{' '}
            {t.medien.angesehen(anzahl(t.medien.datei, t.medien.dateien, gegen.dateien))}
            {/* **Ein abgeschnittener Lauf sagt das.** Sonst sähe eine halbe
                Liste vollständig aus. */}
            {gegen.offen > 0 && t.medien.stehenAus(gegen.offen)}.
          </p>

          {/* **Und der Rest ist erreichbar.** Ein Deckel ohne Weg dahinter
              hieße, dass ab Datei 401 nie jemand nachsieht. */}
          {gegen.weiter !== null && (
            <button
              type="button"
              className="button button--outline"
              disabled={abgleich.isFetching}
              onClick={() => setAb(gegen.weiter)}
            >
              {t.medien.weitersehen}
            </button>
          )}
          {ab !== null && (
            <button
              type="button"
              className="button button--outline"
              disabled={abgleich.isFetching}
              onClick={() => setAb(null)}
            >
              {t.medien.vonVorne}
            </button>
          )}

          {/* **Der Umfang gehört dazu, sonst ist die Zahl irreführend.** Eine
              Datei ohne Eintrag gehört keiner Reise — sie lässt sich nicht nach
              Besitzer einschränken. Die Gegenrichtung schon. */}
          <p className="medien__fussnote">{t.medien.abgleichFussnote}</p>

          {gegen.herrenlos.length > 0 && (
            <>
              {/* **Die Wurzel steht einmal, nicht in jeder Zeile.** Aus dem
                  Betrieb: „entweder den gesamten Pfad angeben, damit man sie
                  am Server findet." Fünfzigmal wiederholt wäre die Liste auf
                  390 Punkten unlesbar -- zusammen mit dem Weg in der Zeile ist
                  es trotzdem der ganze Pfad. */}
              <p className="medien__wurzel">
                {t.medien.wurzelVor}
                <code>{gegen.wurzel}</code>
              </p>

              <ul className="medienliste">
                {gegen.herrenlos.map((datei) => (
                  <li key={datei.name} className="medienliste__zeile">
                    <span className="medienliste__text">
                      <strong>{t.medien.dateiOhneEintrag}</strong>
                      <span className="medienliste__dazu">
                        {groesse(datei.bytes)} · seit {datei.tage} Tagen
                      </span>
                      <code className="medienliste__pfad">{datei.pfad}</code>
                      <span className="medienliste__knoepfe">
                        {/* Ein Verweis und kein Knopf: er öffnet eine Adresse,
                            und ein neuer Reiter lässt die Liste stehen. Er
                            geht an denselben Ursprung, das Session-Cookie
                            fährt also mit. */}
                        <a
                          className="button button--outline button--small"
                          href={`/api/storage/abgleich/datei/${datei.name}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t.medien.ansehen}
                        </a>
                        <button
                          type="button"
                          className="button button--outline button--gefahr button--small"
                          disabled={wegwerfen.isPending}
                          onClick={() => {
                            void (async () => {
                              if (
                                await rueckfrage(
                                  t.medien.dateiLoeschenFrage,
                                  t.medien.dateiLoeschen,
                                )
                              ) {
                                // **Die Liste holt sich diese Karte selbst
                                // nach.** Sie steht auf `enabled: false`, und
                                // react-query siebt eine abgeschaltete Abfrage
                                // sowohl beim Entwerten als auch beim
                                // Nachholen aus -- der Eintrag bliebe stehen,
                                // und der Knopf sähe aus, als hätte er nichts
                                // getan. Der Fehlschlag steht in
                                // `wegwerfen.isError`; hier wird nichts
                                // verschluckt, nur nichts nachgeholt.
                                await wegwerfen.mutateAsync(datei.name).then(
                                  () => abgleich.refetch(),
                                  () => undefined,
                                )
                              }
                            })()
                          }}
                        >
                          {t.medien.loeschen}
                        </button>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>

              {/* Keine stille Absage: schlägt das Löschen fehl, steht es als
                  Text da und nicht nur als ausbleibende Änderung. */}
              {wegwerfen.isError && (
                <p role="alert" className="dienst__fehler">{t.medien.loeschenFehlt}</p>
              )}
            </>
          )}

          {gegen.fehlend.length > 0 && (
            <ul className="medienliste">
              {gegen.fehlend.map((fund) => (
                <li key={fund.id} className="medienliste__zeile">
                  <span className="medienliste__zeichen" aria-hidden="true">
                    {fund.kind === 'video'
                      ? <VideoIcon size={18} />
                      : fund.kind === 'audio'
                        ? <WelleIcon size={18} />
                        : <PhotoIcon size={18} />}
                  </span>
                  <span className="medienliste__text">
                    <strong>
                      {fund.welche === 'datei'
                        ? t.medien.eintragOhneDatei
                        : fund.welche === 'reisebild'
                          ? t.medien.reisebildFehlt
                          : fund.welche === 'vorschaubild'
                            ? t.medien.vorschaubildFehlt
                            : t.medien.standbildFehlt}
                    </strong>
                    <span className="medienliste__dazu">
                      {fund.trip_title} · {groesse(fund.bytes)}
                      {fund.message_id !== null && t.medien.beitrag(fund.message_id)}
                      {fund.highlight_id !== null && t.medien.punkt(fund.highlight_id)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* Nur, wenn es etwas wegzuräumen gibt -- ein fehlendes Standbild
              zählt nicht: das Video spielt weiter. */}
          {kaputte > 0 && (
            <button
              type="button"
              className="button button--outline button--gefahr button--schmal button--oben"
              disabled={raeumen.isPending}
              onClick={() => {
                void (async () => {
                  if (
                    await rueckfrage(
                      t.medien.eintraegeEntfernenFrage(
                        anzahl(t.medien.eintrag, t.medien.eintraege, kaputte),
                      ),
                      // Der Knopf in der Rückfrage sagt dasselbe wie der davor
                      // -- und in derselben Zahlform.
                      t.medien.eintraegeEntfernen(
                        anzahl(t.medien.eintrag, t.medien.eintraege, kaputte),
                      ),
                    )
                  ) {
                    // Aus demselben Grund wie beim Löschen einer Datei: die
                    // Liste holt sich diese Karte selbst nach.
                    await raeumen.mutateAsync().then(
                      () => abgleich.refetch(),
                      () => undefined,
                    )
                  }
                })()
              }}
            >
              {raeumen.isPending
                ? t.medien.wirdEntfernt
                : t.medien.eintraegeEntfernen(
                    anzahl(t.medien.eintrag, t.medien.eintraege, kaputte),
                  )}
            </button>
          )}

          {/* Keine stille Absage: schlägt es fehl, steht es als Text da --
              nicht nur als eine Liste, die gleich lang geblieben ist. */}
          {raeumen.isError && (
            <p role="alert" className="dienst__fehler">{t.medien.raeumenFehlt}</p>
          )}
        </>
      )}
    </div>
  )
}
